import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

const SUPPORTED_LANGS = ['ru', 'en', 'kk'] as const;
type SupportedLang = typeof SUPPORTED_LANGS[number];

/** Default language depends on domain: .ru → ru, everything else → en */
const getDefaultLang = (): SupportedLang => {
  if (typeof window === 'undefined') return 'en';
  const host = window.location.hostname;
  if (host.endsWith('.ru') || host.includes('localhost')) return 'ru';
  return 'en';
};

/** Map any language code to a supported language */
function mapToSupported(code: string | undefined | null): SupportedLang | undefined {
  if (!code) return undefined;
  const lower = code.toLowerCase();
  // Direct match
  if (SUPPORTED_LANGS.includes(lower as SupportedLang)) return lower as SupportedLang;
  // Prefix match (en-US → en, ru-RU → ru)
  const prefix = lower.split('-')[0];
  if (SUPPORTED_LANGS.includes(prefix as SupportedLang)) return prefix as SupportedLang;
  return undefined;
}

// Custom language detector that checks Zustand localStorage and Telegram
const zustandDetector = {
  name: 'zustandStorage',
  lookup(): string | undefined {
    try {
      const raw = localStorage.getItem('language-storage');
      if (raw) {
        const parsed = JSON.parse(raw);
        const lang = parsed?.state?.language;
        return mapToSupported(lang);
      }
    } catch { /* ignore */ }
    return undefined;
  },
};

const telegramDetector = {
  name: 'telegram',
  lookup(): string | undefined {
    try {
      const tg = (window as any).Telegram?.WebApp;
      const langCode = tg?.initDataUnsafe?.user?.language_code;
      return mapToSupported(langCode);
    } catch { /* ignore */ }
    return undefined;
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(zustandDetector);
languageDetector.addDetector(telegramDetector);

i18n
  .use(HttpBackend)
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: getDefaultLang(),
    supportedLngs: [...SUPPORTED_LANGS],
    defaultNS: 'common',
    ns: ['common', 'chat', 'image', 'video', 'account', 'tools', 'team'],

    detection: {
      order: ['zustandStorage', 'telegram', 'navigator'],
      caches: [], // We persist via Zustand, not i18next
    },

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    react: {
      useSuspense: false,
    },
  });

export default i18n;
export { SUPPORTED_LANGS, type SupportedLang };
